/**
 * SAML error screen — query contract (backend redirect target: `/auth/saml/error`).
 *
 * Query params:
 * - `errorCode` (preferred) or `code` or `error` — drives title, copy, and severity.
 * - Optional for email mismatch: `registeredEmail`, `idpEmail` (shows comparison row).
 *
 * Example:
 *  `/auth/saml/error?errorCode=EMAIL_MISMATCH&registeredEmail=a@corp.com&idpEmail=b@gmail.com`
 */

export const SAML_ERROR_VARIANT = {
  ERROR: 'error',
  WARNING: 'warning',
}

/** Normalize backend / OAuth-style codes to config keys. */
export function normalizeSamlErrorCode(raw) {
  if (!raw || typeof raw !== 'string') return 'DEFAULT'
  const trimmed = raw.trim()
  if (!trimmed) return 'DEFAULT'
  return trimmed.toUpperCase().replace(/-/g, '_')
}

/**
 * Maps alternate API codes to canonical config keys.
 * Extend when backend adds aliases.
 */
const CODE_ALIASES = {
  SAML_AUTH_FAILED: 'SAML_VALIDATION_FAILED',
  SSO_FAILED: 'SAML_VALIDATION_FAILED',
  SAML_ERROR: 'SAML_VALIDATION_FAILED',
  GENERIC_SAML_ERROR: 'SAML_VALIDATION_FAILED',
  GENERIC_SSO_ERROR: 'SAML_VALIDATION_FAILED',
  ACCESS_DENIED: 'SAML_VALIDATION_FAILED',
  ACCOUNT_EMAIL_MISMATCH: 'EMAIL_MISMATCH',
  USER_EMAIL_MISMATCH: 'EMAIL_MISMATCH',
}

/**
 * @typedef {Object} SamlErrorDefinition
 * @property {'error'|'warning'} variant
 * @property {string} titleKey — i18n key
 * @property {string} subtitleKey
 * @property {string[]} detailKeys — bullet copy (i18n keys)
 */

/** @type {Record<string, SamlErrorDefinition>} */
const DEFINITIONS = {
  DEFAULT: {
    variant: SAML_ERROR_VARIANT.ERROR,
    titleKey: 'SAML_ERROR_GENERIC_TITLE',
    subtitleKey: 'SAML_ERROR_GENERIC_SUBTITLE',
    detailKeys: ['SAML_ERROR_GENERIC_DETAIL_1', 'SAML_ERROR_GENERIC_DETAIL_2'],
  },
  SAML_VALIDATION_FAILED: {
    variant: SAML_ERROR_VARIANT.ERROR,
    titleKey: 'SAML_ERROR_GENERIC_TITLE',
    subtitleKey: 'SAML_ERROR_GENERIC_SUBTITLE',
    detailKeys: ['SAML_ERROR_GENERIC_DETAIL_1', 'SAML_ERROR_GENERIC_DETAIL_2'],
  },
  EMAIL_MISMATCH: {
    variant: SAML_ERROR_VARIANT.WARNING,
    titleKey: 'SAML_ERROR_EMAIL_TITLE',
    subtitleKey: 'SAML_ERROR_EMAIL_SUBTITLE',
    detailKeys: ['SAML_ERROR_EMAIL_DETAIL_1'],
  },
  USER_DISABLED: {
    variant: SAML_ERROR_VARIANT.ERROR,
    titleKey: 'SAML_ERROR_USER_DISABLED_TITLE',
    subtitleKey: 'SAML_ERROR_USER_DISABLED_SUBTITLE',
    detailKeys: [],
  },
}

/**
 * @param {string | null | undefined} rawCode
 * @returns {SamlErrorDefinition}
 */
export function resolveSamlErrorDefinition(rawCode) {
  const normalized = normalizeSamlErrorCode(rawCode)
  const canonical = CODE_ALIASES[normalized] || normalized
  return DEFINITIONS[canonical] || DEFINITIONS.DEFAULT
}
